import React from "react";
import {
  Box,
  Container,
  Grid,
  Typography,
  Stack,
  Card,
  useTheme,
  useMediaQuery,
  alpha,
} from "@mui/material";
import { keyframes } from "@mui/system";

// Animation for subtle floating effect
const floatAnimation = keyframes`
  0% { transform: translateY(0px); }
  50% { transform: translateY(-10px); }
  100% { transform: translateY(0px); }
`;

// Default features data to use if none is provided
const defaultFeatures = [
  {
    title: "Automated Invoicing",
    desc: "Create professional invoices in seconds with our automated system. Customize templates, set up recurring invoices, and get paid faster with integrated payment options.",
    image:
      "https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?ixlib=rb-4.0.3&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=1170&q=80",
    benefits: [
      "Reduce manual data entry by 80%",
      "Get paid 2x faster with online payments",
      "Automate recurring billing",
    ],
  },
  {
    title: "Inventory Management",
    desc: "Track stock levels in real-time, set up low stock alerts, and manage suppliers all from one dashboard. Never miss a sale due to out-of-stock items again.",
    image:
      "https://images.unsplash.com/photo-1607082350899-7e105aa886ae?ixlib=rb-4.0.3&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=1170&q=80",
    benefits: [
      "Real-time inventory tracking",
      "Automated low stock alerts",
      "Supplier management tools",
    ],
  },
  {
    title: "Financial Reporting",
    desc: "Generate comprehensive financial reports with a single click. Track profitability, monitor cash flow, and make data-driven decisions for your business growth.",
    image:
      "https://images.unsplash.com/photo-1554224155-6726b3ff858f?ixlib=rb-4.0.3&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=1011&q=80",
    benefits: [
      "Profit & loss statements",
      "Cash flow analysis",
      "Customizable report templates",
    ],
  },
];

const SecondFeatures = ({ features = defaultFeatures }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  // Check if features is valid before mapping
  if (!features || !Array.isArray(features) || features.length === 0) {
    return (
      <Box
        sx={{
          backgroundColor: "background.paper",
          py: 10,
          textAlign: "center",
        }}
      >
        <Container maxWidth="lg">
          <Typography variant="h6" color="text.secondary">
            No features available at the moment.
          </Typography>
        </Container>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        backgroundColor: "background.paper",
        py: { xs: 8, md: 12 },
        position: "relative",
        overflow: "hidden",
        "&::before": {
          content: '""',
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          height: "1px",
          background: `linear-gradient(90deg, transparent, ${alpha(
            theme.palette.primary.main,
            0.3
          )}, transparent)`,
        },
      }}
    >
      <Container maxWidth="lg">
        {/* Section Title */}
        <Box textAlign="center" mb={8}>
          <Typography
            variant="h3"
            sx={{
              fontWeight: 800,
              mb: 2,
              background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
              backgroundClip: "text",
              textFillColor: "transparent",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
            }}
          >
            Features to Run Your Business
          </Typography>
          <Typography
            variant="h6"
            color="text.secondary"
            sx={{
              maxWidth: 700,
              mx: "auto",
              lineHeight: 1.6,
            }}
          >
            Everything you need to manage sales, purchases, payments, inventory,
            and compliance — all in one powerful platform.
          </Typography>
        </Box>

        {/* Alternating Feature Layout */}
        <Stack spacing={10}>
          {features.map((feature, index) => (
            <Grid
              container
              key={index}
              spacing={6}
              alignItems="center"
              justifyContent="space-between"
              direction={
                isMobile
                  ? "column-reverse"
                  : index % 2 === 0
                  ? "row-reverse"
                  : "row"
              }
              sx={{
                position: "relative",
                "&:nth-child(odd)::before": {
                  content: '""',
                  position: "absolute",
                  top: -40,
                  left: -40,
                  width: 120,
                  height: 120,
                  borderRadius: "50%",
                  background: `linear-gradient(135deg, ${alpha(
                    theme.palette.primary.light,
                    0.1
                  )} 0%, transparent 100%)`,
                  zIndex: 0,
                },
              }}
            >
              {/* Text Content Grid Item */}
              <Grid item xs={12} md={5}>
                <Card
                  sx={{
                    p: 4,
                    borderRadius: "16px",
                    background: `linear-gradient(135deg, ${
                      theme.palette.background.default
                    } 0%, ${alpha(theme.palette.background.paper, 0.8)} 100%)`,
                    boxShadow: "0 4px 20px rgba(0, 0, 0, 0.05)",
                    border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                    transition: "all 0.3s ease",
                    height: "100%",
                    "&:hover": {
                      transform: "translateY(-5px)",
                      boxShadow: "0 12px 30px rgba(0, 0, 0, 0.1)",
                    },
                  }}
                  elevation={0}
                >
                  <Stack spacing={3}>
                    <Box sx={{ display: "flex", alignItems: "center" }}>
                      <Box
                        sx={{
                          width: 50,
                          height: 50,
                          borderRadius: "14px",
                          background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          color: "white",
                          fontWeight: "bold",
                          fontSize: "1.5rem",
                          mr: 2,
                        }}
                      >
                        {index + 1}
                      </Box>
                      <Typography
                        variant="h5"
                        sx={{
                          fontWeight: 700,
                          color: "text.primary",
                        }}
                      >
                        {feature.title}
                      </Typography>
                    </Box>
                    <Typography
                      variant="body1"
                      color="text.secondary"
                      sx={{
                        lineHeight: 1.7,
                        fontSize: "1.1rem",
                      }}
                    >
                      {feature.desc}
                    </Typography>
                    {feature.benefits && (
                      <Box>
                        <Typography variant="subtitle1" fontWeight={600} mb={1}>
                          Key Benefits:
                        </Typography>
                        <Stack spacing={1}>
                          {feature.benefits.map((benefit, i) => (
                            <Box
                              key={i}
                              sx={{ display: "flex", alignItems: "center" }}
                            >
                              <Box
                                sx={{
                                  width: 6,
                                  height: 6,
                                  borderRadius: "50%",
                                  backgroundColor: "primary.main",
                                  mr: 1.5,
                                }}
                              />
                              <Typography
                                variant="body2"
                                color="text.secondary"
                                fontSize={"14px"}
                              >
                                {benefit}
                              </Typography>
                            </Box>
                          ))}
                        </Stack>
                      </Box>
                    )}
                  </Stack>
                </Card>
              </Grid>
            </Grid>
          ))}
        </Stack>
      </Container>
    </Box>
  );
};

export default SecondFeatures;
